import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '@/components/Header';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Loader2, Search, Save, Check } from 'lucide-react';
import api from '@/utils/api';
import { toast } from 'sonner';

export default function AdminBoostPricesPage() {
  const navigate = useNavigate();
  const [services, setServices] = useState([]);
  const [filteredServices, setFilteredServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedPlatform, setSelectedPlatform] = useState('all');
  const [saving, setSaving] = useState({});

  useEffect(() => {
    fetchServices();
  }, []);

  useEffect(() => {
    filterServices();
  }, [searchQuery, selectedPlatform, services]);

  const fetchServices = async () => {
    try {
      setLoading(true);
      const response = await api.get('/admin/boost-services');
      if (response.data.success) {
        setServices(response.data.services);
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Erreur lors du chargement des services');
    } finally {
      setLoading(false);
    }
  };

  const filterServices = () => {
    let filtered = services;

    if (selectedPlatform !== 'all') {
      filtered = filtered.filter(s => s.platform === selectedPlatform);
    }

    if (searchQuery) {
      filtered = filtered.filter(s =>
        s.name.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    setFilteredServices(filtered);
  };

  const updateService = async (serviceId, updates) => {
    try {
      setSaving(prev => ({ ...prev, [serviceId]: true }));
      
      await api.put(`/admin/boost-services/${serviceId}`, updates);
      
      // Mettre à jour localement
      setServices(prev =>
        prev.map(s =>
          s.service_id === serviceId ? { ...s, ...updates } : s
        )
      );
      
      toast.success('Service mis à jour!', { duration: 1000 });
    } catch (error) {
      console.error('Error:', error);
      toast.error('Erreur lors de la mise à jour');
    } finally {
      setSaving(prev => ({ ...prev, [serviceId]: false }));
    }
  };

  const handlePriceChange = (serviceId, newPrice) => {
    setServices(prev =>
      prev.map(s =>
        s.service_id === serviceId
          ? { ...s, custom_rate: parseFloat(newPrice) || 0 }
          : s
      )
    );
  };

  const handleActiveToggle = async (serviceId, currentActive) => {
    await updateService(serviceId, { active: !currentActive });
  };

  const platforms = ['all', ...new Set(services.map(s => s.platform))];

  if (loading) {
    return (
      <div className="min-h-screen bg-black text-white">
        <Header />
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="h-12 w-12 animate-spin text-[#E50914]" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white pb-20">
      <Header />
      
      <main className="pt-24 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Card className="glass border-zinc-800 mb-6">
          <CardHeader>
            <CardTitle className="text-3xl font-bold text-center">
              🎛️ Gestion des Prix Boost
            </CardTitle>
            <p className="text-center text-zinc-400 mt-2">
              Modifiez les prix et activez/désactivez les services visibles pour les clients
            </p>
          </CardHeader>
          <CardContent>
            {/* Recherche et filtres */}
            <div className="flex gap-4 mb-6">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
                <Input
                  type="text"
                  placeholder="Rechercher un service..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 bg-zinc-800 border-zinc-700 text-white"
                />
              </div>
              <select
                value={selectedPlatform}
                onChange={(e) => setSelectedPlatform(e.target.value)}
                className="bg-zinc-800 border-zinc-700 text-white rounded-md px-4"
              >
                {platforms.map(p => (
                  <option key={p} value={p}>
                    {p === 'all' ? 'Toutes les plateformes' : p}
                  </option>
                ))}
              </select>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-3 gap-4 mb-6">
              <div className="bg-zinc-800 p-4 rounded-lg">
                <p className="text-zinc-400 text-sm">Total Services</p>
                <p className="text-2xl font-bold">{services.length}</p>
              </div>
              <div className="bg-green-900/20 p-4 rounded-lg border border-green-800">
                <p className="text-green-400 text-sm">Actifs</p>
                <p className="text-2xl font-bold text-green-400">
                  {services.filter(s => s.active).length}
                </p>
              </div>
              <div className="bg-red-900/20 p-4 rounded-lg border border-red-800">
                <p className="text-red-400 text-sm">Désactivés</p>
                <p className="text-2xl font-bold text-red-400">
                  {services.filter(s => !s.active).length}
                </p>
              </div>
            </div>

            {/* Tableau des services */}
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-zinc-700">
                    <th className="text-left p-3 text-zinc-400">Actif</th>
                    <th className="text-left p-3 text-zinc-400">Plateforme</th>
                    <th className="text-left p-3 text-zinc-400">Service</th>
                    <th className="text-right p-3 text-zinc-400">Prix Original</th>
                    <th className="text-right p-3 text-zinc-400">Prix Client</th>
                    <th className="text-center p-3 text-zinc-400">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredServices.map((service) => (
                    <tr key={service.service_id} className="border-b border-zinc-800 hover:bg-zinc-800/50">
                      <td className="p-3">
                        <Checkbox
                          checked={service.active}
                          onCheckedChange={() => handleActiveToggle(service.service_id, service.active)}
                          className="border-zinc-600"
                        />
                      </td>
                      <td className="p-3">
                        <Badge variant="outline">{service.platform}</Badge>
                      </td>
                      <td className="p-3 max-w-md">
                        <p className="text-white truncate">{service.name}</p>
                        <p className="text-xs text-zinc-500">ID: {service.service_id}</p>
                      </td>
                      <td className="p-3 text-right text-zinc-400">
                        {service.original_rate.toFixed(2)}$
                      </td>
                      <td className="p-3 text-right">
                        <Input
                          type="number"
                          step="0.01"
                          value={service.custom_rate}
                          onChange={(e) => handlePriceChange(service.service_id, e.target.value)}
                          className="w-24 bg-zinc-800 border-zinc-700 text-white text-right"
                        />
                      </td>
                      <td className="p-3 text-center">
                        <Button
                          size="sm"
                          onClick={() => updateService(service.service_id, {
                            custom_rate: service.custom_rate
                          })}
                          disabled={saving[service.service_id]}
                          className="bg-green-600 hover:bg-green-700"
                        >
                          {saving[service.service_id] ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <Save className="h-4 w-4" />
                          )}
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
